import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Crown, Clock, MapPin, Loader2, Users, FileSpreadsheet, FileText, ChevronRight } from "lucide-react";
import MiniMap from "@/components/MiniMap";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

function fmtDuration(seconds) {
  if (!seconds && seconds !== 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

async function downloadReport(kind, params) {
  try {
    const res = await api.get(`/sadmin/timeclock/export.${kind}`, {
      params,
      responseType: "blob",
    });
    const blob = new Blob([res.data], { type: res.headers["content-type"] });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fichajes_${params.fecha_desde || "inicio"}_${params.fecha_hasta || "hoy"}.${kind === "xlsx" ? "xlsx" : "pdf"}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success(`Descarga iniciada (${kind.toUpperCase()})`);
  } catch (err) {
    toast.error(`No se pudo descargar el ${kind.toUpperCase()}`);
  }
}

export default function SadminTimeClock() {
  const [data, setData] = useState({ entries: [], summary: [] });
  const [loading, setLoading] = useState(false);
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sadmin/timeclock/report", {
        params: { fecha_desde: fechaDesde || undefined, fecha_hasta: fechaHasta || undefined },
      });
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const selected = useMemo(() => {
    if (!selectedUserId) return null;
    const summary = data.summary.find((s) => s.user_id === selectedUserId);
    if (!summary) return null;
    const sessions = data.entries.filter((e) => e.user_id === selectedUserId);
    // Group sessions by day
    const byDay = {};
    for (const s of sessions) {
      const day = (s.entry_at || "").slice(0, 10);
      if (!byDay[day]) byDay[day] = { day, sessions: [], total: 0, open: 0 };
      byDay[day].sessions.push(s);
      if (s.exit_at) byDay[day].total += s.duration_seconds || 0;
      else byDay[day].open += 1;
    }
    const days = Object.values(byDay).sort((a, b) => (b.day || "").localeCompare(a.day || ""));
    return { summary, sessions, days };
  }, [selectedUserId, data]);

  const exportParams = { fecha_desde: fechaDesde || undefined, fecha_hasta: fechaHasta || undefined };

  return (
    <div className="space-y-6" data-testid="sadmin-timeclock">
      <header>
        <div className="label-up text-[#FF4500] inline-flex items-center gap-1.5">
          <Crown size={12} /> Solo super-admin
        </div>
        <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">
          Reporte de fichajes
        </h1>
        <p className="text-zinc-600 text-sm mt-1">
          Registro horario de todos los usuarios con permiso de fichaje.
        </p>
      </header>

      <section className="brutal-card p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="label-up block mb-1.5">Desde</label>
            <input type="date" className="brutal-input w-full"
              value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
          </div>
          <div>
            <label className="label-up block mb-1.5">Hasta</label>
            <input type="date" className="brutal-input w-full"
              value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
          </div>
          <div className="flex items-end">
            <button onClick={load} disabled={loading} data-testid="apply-filter"
              className="brutal-btn-dark w-full inline-flex items-center justify-center gap-2">
              {loading ? <Loader2 size={16} className="animate-spin" /> : "Aplicar"}
            </button>
          </div>
        </div>
        {/* Export buttons */}
        <div className="mt-3 pt-3 border-t border-slate-200 flex flex-wrap items-center gap-2">
          <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mr-1">Exportar reporte</span>
          <button
            type="button"
            onClick={() => downloadReport("xlsx", exportParams)}
            data-testid="export-xlsx"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg font-semibold text-sm text-white bg-emerald-600 hover:bg-emerald-700 transition-colors"
          >
            <FileSpreadsheet size={16} /> Excel
          </button>
          <button
            type="button"
            onClick={() => downloadReport("pdf", exportParams)}
            data-testid="export-pdf"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg font-semibold text-sm text-white bg-rose-600 hover:bg-rose-700 transition-colors"
          >
            <FileText size={16} /> PDF
          </button>
        </div>
      </section>

      {/* Summary per user — clickable rows */}
      <section className="brutal-card p-5">
        <h2 className="font-display font-extrabold text-xl inline-flex items-center gap-2">
          <Users size={18} /> Resumen por usuario
        </h2>
        {!data.summary.length ? (
          <p className="text-sm text-slate-500 mt-3">Sin datos.</p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-200">
            {data.summary.map((s) => (
              <li key={s.user_id}>
                <button
                  type="button"
                  onClick={() => setSelectedUserId(s.user_id)}
                  data-testid={`summary-${s.user_id}`}
                  className="w-full py-3 flex items-center gap-3 text-left hover:bg-slate-50 rounded-lg px-2 -mx-2 transition-colors"
                >
                  <div className="w-11 h-11 rounded-full bg-[#F5A623] text-white flex items-center justify-center font-bold">
                    {(s.user_name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{s.user_name}</div>
                    <div className="text-xs text-slate-500 truncate">{s.user_email}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-extrabold text-2xl text-[#0B2A4A]">
                      {fmtDuration(s.total_seconds)}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                      {s.sessions} {s.sessions === 1 ? "sesión" : "sesiones"}
                      {s.open_sessions > 0 && <span className="text-orange-600"> · {s.open_sessions} abierta</span>}
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-slate-400 flex-shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* All entries */}
      <section className="brutal-card p-5">
        <h2 className="font-display font-extrabold text-xl inline-flex items-center gap-2">
          <Clock size={18} /> Todas las sesiones ({data.entries.length})
        </h2>
        {!data.entries.length ? (
          <p className="text-sm text-slate-500 mt-3">Sin fichajes.</p>
        ) : (
          <ul className="mt-3 space-y-2" data-testid="entries-list">
            {data.entries.map((e) => (
              <li key={e.id} className="border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm">{e.user_name}</div>
                  <div className="text-xs text-slate-500 truncate">{e.user_email}</div>
                </div>
                <div className="text-xs">
                  <div><strong>Entrada:</strong> {fmtDateTime(e.entry_at)}</div>
                  <div className="text-[11px] text-slate-500 inline-flex items-center gap-1 mt-0.5">
                    <MapPin size={10} /> {e.entry_lat.toFixed(5)}, {e.entry_lng.toFixed(5)}
                  </div>
                </div>
                <MiniMap
                  lat={e.entry_lat}
                  lng={e.entry_lng}
                  label={`Entrada · ${e.user_name}`}
                  testId={`map-entry-${e.id}`}
                />
                <div className="text-xs">
                  <div><strong>Salida:</strong> {e.exit_at ? fmtDateTime(e.exit_at) : <span className="text-orange-600">abierta</span>}</div>
                  {e.exit_lat != null && (
                    <div className="text-[11px] text-slate-500 inline-flex items-center gap-1 mt-0.5">
                      <MapPin size={10} /> {e.exit_lat.toFixed(5)}, {e.exit_lng.toFixed(5)}
                    </div>
                  )}
                </div>
                {e.exit_lat != null && (
                  <MiniMap
                    lat={e.exit_lat}
                    lng={e.exit_lng}
                    label={`Salida · ${e.user_name}`}
                    testId={`map-exit-${e.id}`}
                  />
                )}
                <div className="text-right w-20">
                  <div className="font-display font-extrabold text-lg text-[#0B2A4A]">
                    {e.duration_seconds != null ? fmtDuration(e.duration_seconds) : "…"}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Employee detail dialog */}
      <Dialog open={!!selectedUserId} onOpenChange={(v) => !v && setSelectedUserId(null)}>
        <DialogContent
          className="max-w-3xl p-0 overflow-hidden max-h-[90vh] flex flex-col z-[900]"
          data-testid="employee-detail-dialog"
        >
          {selected && (
            <>
              <div className="p-5 border-b border-slate-200 flex items-center gap-4 pr-14 bg-gradient-to-r from-[#0B2A4A] to-[#0E78C2] text-white">
                <div className="w-14 h-14 rounded-full bg-[#F5A623] text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                  {(selected.summary.user_name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-[#FFC740]">
                    Detalle del empleado
                  </div>
                  <DialogTitle asChild>
                    <div className="font-display font-extrabold text-xl truncate">
                      {selected.summary.user_name}
                    </div>
                  </DialogTitle>
                  <div className="text-xs text-white/70 truncate">{selected.summary.user_email}</div>
                </div>
              </div>

              <div className="p-5 border-b border-slate-200 grid grid-cols-3 gap-3 bg-slate-50">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total</div>
                  <div className="font-display font-extrabold text-2xl text-[#0B2A4A]" data-testid="detail-total">
                    {fmtDuration(selected.summary.total_seconds)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Sesiones</div>
                  <div className="font-display font-extrabold text-2xl text-[#0B2A4A]">
                    {selected.summary.sessions}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Abiertas</div>
                  <div className={`font-display font-extrabold text-2xl ${selected.summary.open_sessions ? "text-orange-600" : "text-[#0B2A4A]"}`}>
                    {selected.summary.open_sessions}
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="detail-days">
                {selected.days.length === 0 ? (
                  <p className="text-sm text-slate-500">Sin sesiones en el rango.</p>
                ) : selected.days.map((d) => (
                  <div key={d.day} className="border border-slate-200 rounded-xl overflow-hidden">
                    <div className="px-4 py-2 bg-slate-100 flex items-center justify-between">
                      <div className="font-semibold text-sm text-[#0B2A4A]">
                        {d.day ? new Date(d.day + "T00:00:00").toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </div>
                      <div className="text-xs font-semibold text-slate-600">
                        {fmtDuration(d.total)}
                        {d.open > 0 && <span className="text-orange-600 ml-2">· {d.open} abierta</span>}
                      </div>
                    </div>
                    <ul className="divide-y divide-slate-100">
                      {d.sessions.map((s) => (
                        <li key={s.id} className="p-3 flex flex-wrap items-center gap-3">
                          <div className="text-xs flex-1 min-w-[140px]">
                            <div><strong>Entrada:</strong> {new Date(s.entry_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}</div>
                            <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                              <MapPin size={10} /> {s.entry_lat.toFixed(5)}, {s.entry_lng.toFixed(5)}
                            </div>
                          </div>
                          <MiniMap
                            lat={s.entry_lat}
                            lng={s.entry_lng}
                            label={`Entrada · ${s.user_name}`}
                            testId={`detail-map-entry-${s.id}`}
                          />
                          <div className="text-xs flex-1 min-w-[140px]">
                            <div><strong>Salida:</strong> {s.exit_at ? new Date(s.exit_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) : <span className="text-orange-600">abierta</span>}</div>
                            {s.exit_lat != null && (
                              <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                                <MapPin size={10} /> {s.exit_lat.toFixed(5)}, {s.exit_lng.toFixed(5)}
                              </div>
                            )}
                          </div>
                          {s.exit_lat != null && (
                            <MiniMap
                              lat={s.exit_lat}
                              lng={s.exit_lng}
                              label={`Salida · ${s.user_name}`}
                              testId={`detail-map-exit-${s.id}`}
                            />
                          )}
                          <div className="text-right w-20">
                            <div className="font-display font-extrabold text-base text-[#0B2A4A]">
                              {s.duration_seconds != null ? fmtDuration(s.duration_seconds) : "…"}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
